import { useEffect, useMemo, useState } from 'react';
import { useResource } from '../../hooks/useResource';
import { api } from '../../lib/api';
import { t } from '../../lib/i18n';
import type { Inbox } from '../../lib/types';
import { InboxPicker } from './InboxPicker';
import { sx } from './styles';

interface PriorityMultipliers {
  urgent: number;
  high: number;
  medium: number;
  low: number;
}

interface SlaConfig {
  firstResponseTargetMin: number;
  resolutionTargetMin: number;
  prioritySlaMultipliers: PriorityMultipliers;
}

interface InboxWithSla extends Inbox {
  sla?: Partial<SlaConfig>;
}

const DEFAULT_SLA: SlaConfig = {
  firstResponseTargetMin: 60,
  resolutionTargetMin: 1440,
  prioritySlaMultipliers: { urgent: 0.25, high: 0.5, medium: 1, low: 2 },
};

export function SlaInboxPanel({ canEdit }: { canEdit: boolean }) {
  const { items: inboxes, loading } = useResource<InboxWithSla>('inboxes');
  const [inboxId, setInboxId] = useState<string | null>(null);
  const [draft, setDraft] = useState<SlaConfig>(DEFAULT_SLA);
  const [saving, setSaving] = useState(false);

  const selected = useMemo(() => inboxes.find((i) => i.id === inboxId) || null, [inboxes, inboxId]);

  useEffect(() => {
    if (!inboxId && inboxes.length > 0) setInboxId(inboxes[0].id);
  }, [inboxes, inboxId]);

  useEffect(() => {
    if (!selected) return;
    const s = selected.sla;
    setDraft({
      firstResponseTargetMin: s?.firstResponseTargetMin ?? DEFAULT_SLA.firstResponseTargetMin,
      resolutionTargetMin: s?.resolutionTargetMin ?? DEFAULT_SLA.resolutionTargetMin,
      prioritySlaMultipliers: {
        ...DEFAULT_SLA.prioritySlaMultipliers,
        ...(s?.prioritySlaMultipliers as Partial<PriorityMultipliers> | undefined),
      },
    });
  }, [selected]);

  async function save() {
    if (!inboxId) return;
    setSaving(true);
    try {
      await api.patch('inboxes', inboxId, { sla: draft });
    } catch (e) {
      alert(`Save failed: ${e instanceof Error ? e.message : e}`);
    } finally {
      setSaving(false);
    }
  }

  return (
    <section>
      <h2 style={{ margin: 0, fontSize: 18, marginBottom: 12 }}>{t('settings.sla.title')}</h2>
      {loading && <div>{t('common.loading')}</div>}
      <InboxPicker inboxes={inboxes} value={inboxId} onChange={setInboxId} />

      {selected && (
        <>
          <div style={sx.card}>
            <label style={sx.label}>{t('settings.sla.firstResponse')}</label>
            <input
              type="number"
              min={1}
              value={draft.firstResponseTargetMin}
              onChange={(e) =>
                setDraft((d) => ({ ...d, firstResponseTargetMin: Number(e.target.value) || 0 }))
              }
              disabled={!canEdit}
              style={{ ...sx.input, maxWidth: 160 }}
            />
            <label style={{ ...sx.label, marginTop: 12 }}>{t('settings.sla.resolution')}</label>
            <input
              type="number"
              min={1}
              value={draft.resolutionTargetMin}
              onChange={(e) =>
                setDraft((d) => ({ ...d, resolutionTargetMin: Number(e.target.value) || 0 }))
              }
              disabled={!canEdit}
              style={{ ...sx.input, maxWidth: 160 }}
            />
          </div>

          <div style={sx.card}>
            <label style={sx.label}>{t('settings.sla.multipliers')}</label>
            <table style={sx.table}>
              <thead>
                <tr>
                  <th style={sx.th}>{t('settings.automations.trigger')}</th>
                  <th style={sx.th}>×</th>
                </tr>
              </thead>
              <tbody>
                {(['urgent', 'high', 'medium', 'low'] as const).map((p) => (
                  <tr key={p}>
                    <td style={sx.td}>{t(`settings.sla.priority.${p}`)}</td>
                    <td style={sx.td}>
                      <input
                        type="number"
                        step={0.05}
                        min={0}
                        value={draft.prioritySlaMultipliers[p]}
                        onChange={(e) =>
                          setDraft((d) => ({
                            ...d,
                            prioritySlaMultipliers: {
                              ...d.prioritySlaMultipliers,
                              [p]: Number(e.target.value) || 0,
                            },
                          }))
                        }
                        disabled={!canEdit}
                        style={{ ...sx.input, maxWidth: 100 }}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {canEdit && (
            <button onClick={save} disabled={saving} style={sx.btnPrimary}>
              {saving ? t('common.loading') : t('common.save')}
            </button>
          )}
        </>
      )}
    </section>
  );
}
